<template>
  <span
    ref="barElement"
    class="period-bar"
    :class="barClasses"
    :role="role"
    :tabindex="tabindex"
    :aria-label="accessibleLabel"
    @click="handleClick"
    @keydown.enter.prevent="handleKeyActivate"
    @keydown.space.prevent="handleKeyActivate"
    @mouseenter="handleHover(true)"
    @mouseleave="handleHover(false)"
    @focus="handleHover(true)"
    @blur="handleHover(false)"
  >
    <span v-if="showLabel" class="period-bar__label">{{ visibleLabel }}</span>
    <span v-if="showOpenEndedMarker" class="period-bar__marker">
      <svg
        v-if="showDashedMarker"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2 6h2M6 6h2M10 6h2"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-dasharray="2 2"
        />
      </svg>
      <svg
        v-else
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2 3l3 3-3 3M6 3l3 3-3 3"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
  </span>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import {
  formatPeriodBarAriaLabel,
  normalizePeriodBarState,
} from "./periodBarUtils";

const props = defineProps({
  state: {
    type: String,
    required: true,
  },
  openEnded: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    required: true,
  },
  /** Secondary text (e.g. a pay range) appended to the label only while the bar is wide enough. */
  detail: {
    type: String,
    default: "",
  },
  selectable: {
    type: Boolean,
    default: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  /** Draft only: dashed purple outline on a light tint instead of the solid timeline fill; an open-ended bar shows » instead of the dashed marker. */
  outlined: {
    type: Boolean,
    default: false,
  },
  /** Highlighted because its row is hovered or focused (bar ↔ row mirroring). */
  hovered: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["select", "hover"]);

const barElement = ref(null);
const isNarrow = ref(false);
const barWidth = ref(null);

const LABEL_CHAR_WIDTH_PX = 6.5;
const BAR_CHROME_PX = 18;
const OPEN_ENDED_MARKER_PX = 16;

const normalizedState = computed(() => normalizePeriodBarState(props.state));

const role = computed(() => (props.selectable ? "button" : "img"));

const tabindex = computed(() => (props.selectable ? "0" : null));

const barClasses = computed(() => {
  const classes = [`period-bar--${normalizedState.value}`];
  if (props.selectable && props.selected) {
    classes.push("period-bar--selected");
  }
  if (!props.selectable) {
    classes.push("period-bar--non-selectable");
  }
  if (props.hovered) {
    classes.push("period-bar--hovered");
  }
  if (props.outlined && normalizedState.value === "draft") {
    classes.push("period-bar--outlined");
  }
  return classes;
});

const showLabel = computed(() => !isNarrow.value);

const showOpenEndedMarker = computed(() => props.openEnded);

const showDashedMarker = computed(() => normalizedState.value === "draft" && !props.outlined);

const fullLabel = computed(() =>
  [props.label, props.detail].filter(Boolean).join(" · ")
);

const detailFits = computed(() => {
  if (!props.detail || barWidth.value === null) return false;
  const markerWidth = props.openEnded ? OPEN_ENDED_MARKER_PX : 0;
  const needed =
    fullLabel.value.length * LABEL_CHAR_WIDTH_PX + BAR_CHROME_PX + markerWidth;
  return barWidth.value >= needed;
});

const visibleLabel = computed(() => (detailFits.value ? fullLabel.value : props.label));

const accessibleLabel = computed(() =>
  formatPeriodBarAriaLabel(fullLabel.value, normalizedState.value, props.openEnded)
);

function handleClick() {
  if (props.selectable) {
    emit("select");
  }
}

function handleKeyActivate() {
  if (props.selectable) {
    emit("select");
  }
}

function handleHover(isHovered) {
  emit("hover", isHovered);
}

function checkWidth() {
  if (barElement.value) {
    const width = barElement.value.getBoundingClientRect().width;
    barWidth.value = width;
    isNarrow.value = width < 28;
  }
}

let resizeObserver = null;

onMounted(() => {
  checkWidth();
  if (window.ResizeObserver && barElement.value) {
    resizeObserver = new ResizeObserver(checkWidth);
    resizeObserver.observe(barElement.value);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver && barElement.value) {
    resizeObserver.unobserve(barElement.value);
    resizeObserver.disconnect();
  }
});

defineExpose({
  checkWidth,
  isNarrow,
});
</script>

<style scoped>
.period-bar {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  padding: 4px 8px;
  margin: 0;
  overflow: hidden;
  border-style: solid;
  cursor: default;
  outline: none;
  transition: box-shadow 0.15s ease;
}

.period-bar__label {
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  font-family: Inter, sans-serif;
  font-size: 11px;
  line-height: 13.31px;
  font-style: normal;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.period-bar__marker {
  flex-shrink: 0;
  width: 12px;
}

/* Draft state */
.period-bar--draft {
  background: var(--component-colors-utility-fuchsia-utility-fuchsia-50, #fdf4ff);
  border-width: 1.5px;
  border-style: dashed;
  border-color: var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
  border-radius: 10px;
  color: var(--colors-text-text-primary-900, #101828);
}

.period-bar--draft .period-bar__label {
  font-weight: 600;
  color: var(--colors-text-text-primary-900, #101828);
}

.period-bar--draft .period-bar__marker {
  color: var(--colors-text-text-primary-900, #101828);
}

/* Draft outlined variant (draft drawer header) */
.period-bar--draft.period-bar--outlined {
  background: #f9f5ff;
  border-width: 2px;
  border-color: #7f56d9;
}

.period-bar--draft.period-bar--outlined .period-bar__label,
.period-bar--draft.period-bar--outlined .period-bar__marker {
  color: var(--colors-text-text-primary-900, #101828);
}

/* Current state */
.period-bar--current {
  background: #dcfae6;
  border-width: 1px;
  border-color: #75e0a7;
  border-radius: 4px;
  color: #067647;
}

.period-bar--current .period-bar__label {
  font-weight: 500;
}

/* Future state */
.period-bar--future {
  background: #eff8ff;
  border-width: 1px;
  border-color: #b2ddff;
  border-radius: 4px;
  color: #175cd3;
}

.period-bar--future .period-bar__label {
  font-weight: 500;
}

/* Lapsed state */
.period-bar--lapsed {
  background: #f2f4f7;
  border-width: 1px;
  border-color: #d0d5dd;
  border-radius: 4px;
  color: #344054;
}

.period-bar--lapsed .period-bar__label {
  font-weight: 500;
}

/* Selectable hover state */
.period-bar:not(.period-bar--non-selectable):hover {
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1);
}

/* Hovered-from-row state (the bar highlights when its PayPlanRowCard is hovered) */
.period-bar--hovered {
  box-shadow: 0 0 0 3px #d6bbfb;
}

/* Selected state */
.period-bar--selected {
  box-shadow: 0 0 0 3px #7f56d9;
}

.period-bar--selected .period-bar__label {
  font-weight: 600;
}

/* Focus state (keyboard navigation) */
.period-bar:not(.period-bar--non-selectable):focus-visible {
  border-width: 2px;
  border-color: #7f56d9;
  box-shadow: 0 0 0 3px #7f56d9;
}

/* Draft keeps its fuchsia dashed border when selected or focused (Figma: 2px #d444f1). */
.period-bar--draft.period-bar--selected {
  border-width: 2px;
}

.period-bar--draft:not(.period-bar--non-selectable):focus-visible {
  border-color: var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
}

/* Non-selectable styling */
.period-bar--non-selectable {
  cursor: default;
}

.period-bar--non-selectable:hover {
  box-shadow: none;
}
</style>
